import React, { useState, useCallback, useEffect } from "react";
import {
  getUserPolicySettings,
  ModuleNames,
  ModulePolicyTypes,
  UserPolicyEntity,
  UserPolicyTypes,
} from "lib/api/userSettings";

interface UserDataContextValue {
  userPolicies?: UserPolicyEntity[];
  setUserPolicies: (userPolicy: UserPolicyEntity[]) => void;
  reloadPolicies: () => void;
  hasSystemTypePolicy: (
    typeName: string,
    policyAction: UserPolicyTypes
  ) => boolean;
  hasSystemTypeNamePolicy: (
    typeId: string,
    policyAction: UserPolicyTypes
  ) => boolean;
  hasDomainModule: (module: ModuleNames) => boolean;
  hasModulePolicy: (
    module: ModuleNames,
    policyModule: ModulePolicyTypes
  ) => boolean;
}
export const UserDataContext = React.createContext<UserDataContextValue>({
  userPolicies: undefined,
  setUserPolicies: () => undefined,
  reloadPolicies: () => undefined,
  hasSystemTypePolicy: () => false,
  hasSystemTypeNamePolicy: () => false,
  hasDomainModule: () => false,
  hasModulePolicy: () => false,
});

export const UserDataProvider: React.FC<{
  children: React.ReactNode;
  policies: UserPolicyEntity[] | undefined;
}> = ({ children, policies }) => {
  const [userPolicies, setUserPolicies] = useState(policies);
  const [keycloakReady, setkeycloakReady] = useState(false);
  useEffect(() => {
    if (window.KEYCLOAK) {
      if (window.KEYCLOAK.authenticated) {
        setkeycloakReady(true);
      } else {
        window.KEYCLOAK.onReady = () => {
          setkeycloakReady(true);
        };
      }
    }
  }, []);
  const reloadPolicies = useCallback(() => {
    if (keycloakReady) {
      // try {
      getUserPolicySettings()
        .then((data) => {
          setUserPolicies(data.data.data);
        })
        .catch((reason) => {
          // eslint-disable-next-line no-console
          console.error(`error get new user policy, ${reason}`);
        });

      // } catch (e) {
      // eslint-disable-next-line no-console
      //  console.error(`error get new user policy, ${e}`);
      // }
    }
  }, [keycloakReady]);
  useEffect(() => {
    reloadPolicies();
  }, [reloadPolicies, policies, keycloakReady]);
  const hasModulePolicy = useCallback(
    (module: ModuleNames, policyModule: ModulePolicyTypes): boolean => {
      const policy = userPolicies?.find((up) => {
        return (
          !up.expired &&
          up.type === "module" &&
          up.name === module &&
          (up.operations?.find((op) => {
            return op === `${policyModule}`;
          }) ??
            false)
        );
      });
      return policy !== undefined;
    },
    [userPolicies]
  );

  const hasSystemTypePolicy = useCallback(
    (typeId: string, policyAction: UserPolicyTypes): boolean => {
      const policy = userPolicies?.find((up) => {
        return (
          !up.expired &&
          up.type_id === typeId &&
          (up.operations?.find((op) => {
            return op === `${typeId}_${policyAction}`;
          }) ??
            false)
        );
      });
      return policy !== undefined;
    },
    [userPolicies]
  );

  const hasSystemTypeNamePolicy = useCallback(
    (typeName: string, policyAction: UserPolicyTypes): boolean => {
      const policy = userPolicies?.find((up) => {
        return (
          !up.expired &&
          up?.name?.toLowerCase() === typeName?.toLowerCase() &&
          (up.operations?.find((op) => {
            return op === `${up.type_id}_${policyAction}`;
          }) ??
            false)
        );
      });
      return policy !== undefined;
    },
    [userPolicies]
  );

  const hasDomainModule = useCallback(
    (module: ModuleNames): boolean => {
      const policy = userPolicies?.find((up) => {
        return !up.expired && up.type === "module" && up.name === module;
      });
      return policy !== undefined;
    },
    [userPolicies]
  );

  return (
    <UserDataContext.Provider
      value={{
        userPolicies,
        setUserPolicies,
        reloadPolicies,
        hasSystemTypePolicy,
        hasSystemTypeNamePolicy,
        hasDomainModule,
        hasModulePolicy,
      }}
    >
      {children}
    </UserDataContext.Provider>
  );
};
